import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, ClipboardCheck, MessageCircleQuestion, PenLine, Search, Send, XCircle } from 'lucide-react';
import {
  edgeFunctions, freigabeSchritt, getCampaign, listCampaignHistory, listCampaigns, listRecentHistory,
  listReviewCampaigns, listSegments, type Campaign, type HistoryEntry,
} from '../../lib/supabase';
import { aktionText, standVon, wartetTage } from '../../lib/freigabe';
import { formatDateTime } from '../../lib/format';
import { istRedaktion } from '../../lib/rollen';
import { useAuthStore } from '../../stores/authStore';
import { useFreigabenStore } from '../../stores/freigabenStore';
import { PageHeader } from '../../components/PageHeader';
import { FreigabeBadge } from '../../components/Freigabe';
import { MailDetail } from '../../components/MailDetail/MailDetail';
import { useToast } from '../../components/Toast';
import { EntscheidenDialog, type Entscheidung } from './EntscheidenDialog';
import './Freigaben.css';

type Reiter = 'offen' | 'entschieden' | 'protokoll';
// Selbst Verschicktes (Recht „ohne Freigabe versenden") steht mit dabei, damit die Redaktion es sieht.
const ENTSCHEIDUNGEN = ['freigegeben', 'rueckfrage', 'abgelehnt', 'selbst_versendet'];

function tageText(iso: string | null | undefined): string {
  if (!iso) return '';
  const tage = Math.round((new Date(iso).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86_400_000);
  if (tage === 0) return 'heute';
  if (tage === 1) return 'morgen';
  if (tage > 1) return `in ${tage} Tagen`;
  return tage === -1 ? 'gestern' : `vor ${-tage} Tagen`;
}

function sortiert(liste: Campaign[]): Campaign[] {
  return [...liste].sort((a, b) => {
    if (!!a.is_urgent !== !!b.is_urgent) return a.is_urgent ? -1 : 1;
    const wa = a.desired_send_at ? Date.parse(a.desired_send_at) : Infinity;
    const wb = b.desired_send_at ? Date.parse(b.desired_send_at) : Infinity;
    return wa - wb;
  });
}

export function Freigaben() {
  const [params, setParams] = useSearchParams();
  const reiter = (['offen', 'entschieden', 'protokoll'].includes(params.get('reiter') ?? '') ? params.get('reiter') : 'offen') as Reiter;
  const mailId = params.get('mail');
  const user = useAuthStore((s) => s.user);
  const ladeZahl = useFreigabenStore((s) => s.laden);
  const qc = useQueryClient();
  const toast = useToast();
  const [dialog, setDialog] = useState<Entscheidung | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [suche, setSuche] = useState('');

  const liste = useQuery({ queryKey: ['review-campaigns'], queryFn: listReviewCampaigns, refetchInterval: 30_000 });
  const detail = useQuery({ queryKey: ['campaign', mailId], queryFn: () => getCampaign(mailId!), enabled: !!mailId });
  const verlauf = useQuery({ queryKey: ['campaign-history', mailId], queryFn: () => listCampaignHistory(mailId!), enabled: !!mailId });
  const gruppen = useQuery({ queryKey: ['segments'], queryFn: listSegments });
  const protokoll = useQuery({ queryKey: ['recent-history'], queryFn: () => listRecentHistory(300), enabled: reiter !== 'offen' });
  const alle = useQuery({ queryKey: ['campaigns'], queryFn: listCampaigns, enabled: reiter !== 'offen' });

  const kampagnen = useMemo(() => liste.data ?? [], [liste.data]);
  const warten = sortiert(kampagnen.filter((c) => c.status === 'pending_approval' && c.review_state !== 'freigegeben'));
  const haengt = kampagnen.filter((c) => c.review_state === 'freigegeben' && (c.status === 'pending_approval' || c.status === 'failed'));
  const beiPerson = kampagnen.filter((c) => c.status === 'draft' && c.review_state === 'rueckfrage');

  const betreffVon = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of [...(alle.data ?? []), ...kampagnen]) m.set(c.id, c.original_subject || c.subject || c.name);
    return m;
  }, [alle.data, kampagnen]);

  const setzen = (werte: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, w] of Object.entries(werte)) { if (w) p.set(k, w); else p.delete(k); }
    setParams(p);
  };

  const neuLaden = (id: string) => {
    void qc.invalidateQueries({ queryKey: ['review-campaigns'] });
    void qc.invalidateQueries({ queryKey: ['campaign', id] });
    void qc.invalidateQueries({ queryKey: ['campaign-history', id] });
    void qc.invalidateQueries({ queryKey: ['recent-history'] });
    void qc.invalidateQueries({ queryKey: ['campaigns'] });
    void ladeZahl();
  };

  const c = detail.data ?? null;
  const eigene = !!c && c.created_by === user?.id && !!c.requires_approval;
  const stand = c ? standVon(c) : null;
  const erwartet = c ? (c.review_state ?? c.status) : null;

  const schritt = async (fn: () => Promise<void>, id: string) => {
    setLaeuft(true);
    try {
      await fn();
      setDialog(null);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLaeuft(false);
      neuLaden(id);
    }
  };

  const inPruefung = (m: Campaign) => schritt(async () => {
    await freigabeSchritt(m.id, 'in_pruefung', null, m.review_state ?? m.status);
    toast.success('In Prüfung genommen — die anderen sehen, dass Du dran bist.');
  }, m.id);

  const bestaetigen = ({ grund, zeitpunkt }: { grund: string | null; zeitpunkt: string | null }) => {
    if (!c || !dialog) return;
    const id = c.id;
    void schritt(async () => {
      if (dialog === 'freigeben' || dialog === 'versand') {
        if (dialog === 'freigeben') await freigabeSchritt(id, 'freigeben', null, erwartet);
        try {
          await edgeFunctions.sendCampaign(id, zeitpunkt ?? undefined);
        } catch (e) {
          // Freigabe steht, nur das Einplanen hakt: Die Person erfährt es
          // erst, wenn der Versand wirklich eingeplant ist.
          toast.error(`Freigegeben, aber der Versand ließ sich nicht einplanen: ${(e as Error).message} — unter „Offen" erneut anstoßen.`);
          return;
        }
        await edgeFunctions.notifyReviewDecision(id, zeitpunkt).catch((e) => console.warn('Benachrichtigung:', e));
        toast.success(zeitpunkt ? `Freigegeben — geht am ${formatDateTime(zeitpunkt)} raus.` : 'Freigegeben — der Versand läuft.');
      } else {
        await freigabeSchritt(id, dialog === 'rueckfrage' ? 'rueckfrage' : 'ablehnen', grund, erwartet);
        await edgeFunctions.notifyReviewDecision(id).catch((e) => console.warn('Benachrichtigung:', e));
        toast.success(dialog === 'rueckfrage' ? 'Rückfrage gesendet.' : 'Abgelehnt — die Person bekommt Deine Begründung.');
      }
    }, id);
  };

  if (!istRedaktion(user)) {
    return <div className="empty-state"><h3>Nur für die Redaktion</h3><p>Freigeben dürfen Admins und wer in den Einstellungen das Freigaberecht hat.</p></div>;
  }

  const aktionen = c && stand ? (
    eigene ? (
      <span className="fg-hinweis-text">Das ist Deine eigene Mail — freigeben muss jemand anderes aus der Redaktion (Vier-Augen-Prinzip).</span>
    ) : (
      <>
        {stand === 'eingereicht' && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => void inPruefung(c)} disabled={laeuft}><Search size={14} aria-hidden="true" /> In Prüfung nehmen</button>
        )}
        {(stand === 'eingereicht' || stand === 'in_pruefung' || stand === 'rueckfrage') && (
          <>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setDialog('freigeben')} disabled={laeuft}><CheckCircle2 size={14} aria-hidden="true" /> Freigeben …</button>
            {stand !== 'rueckfrage' && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDialog('rueckfrage')} disabled={laeuft}><MessageCircleQuestion size={14} aria-hidden="true" /> Rückfrage …</button>
            )}
            <button type="button" className="btn btn-ghost btn-sm fg-gefahr" onClick={() => setDialog('ablehnen')} disabled={laeuft}><XCircle size={14} aria-hidden="true" /> Ablehnen …</button>
          </>
        )}
        {(stand === 'eingereicht' || stand === 'in_pruefung') && c.builder_template && (
          <Link to={`/neue-mail/${c.id}`} className="btn btn-ghost btn-sm"><PenLine size={14} aria-hidden="true" /> Im Assistenten korrigieren</Link>
        )}
        {(stand === 'freigegeben' || (stand === 'fehler' && c.review_state === 'freigegeben')) && (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setDialog('versand')} disabled={laeuft}><Send size={14} aria-hidden="true" /> Versand anstoßen …</button>
        )}
        {!['eingereicht', 'in_pruefung', 'rueckfrage', 'freigegeben', 'fehler'].includes(stand) && (
          <span className="fg-hinweis-text">Hier gibt es nichts mehr zu entscheiden.</span>
        )}
      </>
    )
  ) : null;

  const zeile = (m: Campaign) => {
    const t = wartetTage(m);
    return (
      <button key={m.id} type="button" className={`mm-eintrag${m.id === mailId ? ' aktiv' : ''}`} onClick={() => setzen({ mail: m.id })} aria-current={m.id === mailId ? 'true' : undefined}>
        <span className="mm-eintrag-kopf">
          <FreigabeBadge campaign={m} titel={false} />
          {m.is_urgent && <span className="badge badge-danger">Eilt</span>}
          {t !== null && m.status === 'pending_approval' && <span className="mm-wer">wartet {t === 0 ? 'seit heute' : `seit ${t} ${t === 1 ? 'Tag' : 'Tagen'}`}</span>}
        </span>
        <strong className="mm-betreff">{m.original_subject || m.subject || m.name}</strong>
        <span className="mm-meta">
          {m.created_by_name ?? '—'}{m.target_studios?.length ? ` · ${m.target_studios.join(', ')}` : ''}
          {m.desired_send_at ? ` · Wunsch ${formatDateTime(m.desired_send_at)} (${tageText(m.desired_send_at)})` : ''}
        </span>
      </button>
    );
  };

  const entschieden = (protokoll.data ?? []).filter((h) => ENTSCHEIDUNGEN.includes(h.action));
  const filterSuche = (h: HistoryEntry) => {
    const q = suche.trim().toLowerCase();
    if (!q) return true;
    return [betreffVon.get(h.campaign_id), h.actor_name, h.message, aktionText(h)].some((x) => x?.toLowerCase().includes(q));
  };

  return (
    <div>
      <PageHeader
        title="Freigaben"
        subtitle="Mails von Geschäftsführung und Studioleitung prüfen und freigeben — erst dann gehen sie raus."
      />

      <div className="seg-control fg-reiter" role="tablist" aria-label="Freigaben" data-tour="freigaben-reiter">
        {([['offen', `Offen (${warten.length + haengt.length})`], ['entschieden', 'Entschieden'], ['protokoll', 'Protokoll']] as [Reiter, string][]).map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={reiter === id} className={`seg-btn${reiter === id ? ' active' : ''}`} onClick={() => setzen({ reiter: id === 'offen' ? null : id, mail: null })}>
            {label}
          </button>
        ))}
      </div>

      {reiter === 'offen' && (
        <div className={`mm-layout${mailId ? ' mm-layout--detail' : ''}`}>
          <div className="mm-liste" data-tour="freigaben-liste">
            {liste.isLoading && <div className="loading-spinner" style={{ margin: '2rem auto' }} />}
            {!liste.isLoading && warten.length + haengt.length + beiPerson.length === 0 && (
              <div className="fg-leer"><ClipboardCheck size={28} aria-hidden="true" /><p>Nichts zu tun — gerade wartet keine Mail auf eine Entscheidung.</p></div>
            )}
            {warten.length > 0 && <h3 className="fg-gruppe">Warten auf eine Entscheidung</h3>}
            {warten.map(zeile)}
            {haengt.length > 0 && <h3 className="fg-gruppe">Freigegeben, Versand nicht eingeplant</h3>}
            {haengt.map(zeile)}
            {beiPerson.length > 0 && <h3 className="fg-gruppe">Rückfrage offen — die Person ist dran</h3>}
            {beiPerson.map(zeile)}
          </div>
          <div className="mm-detail" data-tour="freigaben-detail">
            {!mailId ? (
              <div className="mm-platzhalter">Wähl links eine Mail — hier stehen dann Vorschau, Vorab-Antworten und die Knöpfe zum Entscheiden.</div>
            ) : detail.isLoading ? (
              <div className="loading-spinner" style={{ margin: '2rem auto' }} />
            ) : !c ? (
              <div className="mm-platzhalter">Diese Mail gibt es nicht (mehr).</div>
            ) : (
              <>
                <button type="button" className="btn btn-ghost btn-sm mm-zurueck" onClick={() => setzen({ mail: null })}><ArrowLeft size={14} aria-hidden="true" /> Zur Liste</button>
                <MailDetail c={c} verlauf={verlauf.data ?? []} verlaufLaedt={verlauf.isLoading} gruppen={gruppen.data ?? []} sicht="redaktion" aktionen={aktionen} tourAktionen="freigaben-entscheiden" />
              </>
            )}
          </div>
        </div>
      )}

      {reiter !== 'offen' && (
        <div className="card" data-tour={reiter === 'protokoll' ? 'freigaben-protokoll' : undefined}>
          <div className="search-input-wrapper" style={{ marginBottom: '1rem' }}>
            <Search size={16} aria-hidden="true" />
            <input type="search" className="search-input" placeholder="Suche nach Betreff, Person oder Begründung …" value={suche} onChange={(e) => setSuche(e.target.value)} aria-label="Suche" />
          </div>
          {protokoll.isLoading ? <div className="loading-spinner" style={{ margin: '2rem auto' }} /> : (
            <div className="table-scroll">
              <table className="data-table">
                <thead><tr><th>Wann</th><th>Mail</th><th>Schritt</th><th>Wer</th><th>Begründung</th></tr></thead>
                <tbody>
                  {(reiter === 'entschieden' ? entschieden : protokoll.data ?? []).filter(filterSuche).map((h) => (
                    <tr key={h.id} className="clickable" onClick={() => setzen({ reiter: null, mail: h.campaign_id })}>
                      <td className="fg-nowrap">{formatDateTime(h.created_at)}</td>
                      <td>{betreffVon.get(h.campaign_id) ?? <em>gelöschte Mail</em>}</td>
                      <td>{aktionText(h)}</td>
                      <td>{h.actor_name ?? 'Mailtool'}</td>
                      <td className="fg-grund">{h.message ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {dialog && c && (
        <EntscheidenDialog art={dialog} c={c} laeuft={laeuft} onAbbrechen={() => setDialog(null)} onBestaetigen={bestaetigen} />
      )}
    </div>
  );
}
